import { useState } from 'react';
import { Twitter, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const MAX_CHARS = 280;

interface Tweet {
  id: string;
  content: string;
}

const TwitterCounter = () => {
  const [threadMode, setThreadMode] = useState(false);
  const [singleTweet, setSingleTweet] = useState('');
  const [tweets, setTweets] = useState<Tweet[]>([{ id: '1', content: '' }]);

  const getCharInfo = (text: string) => {
    const count = text.length;
    const remaining = MAX_CHARS - count;
    const percentage = (count / MAX_CHARS) * 100;
    return { count, remaining, percentage };
  };

  const addTweet = () => {
    setTweets([...tweets, { id: Date.now().toString(), content: '' }]);
  };

  const removeTweet = (id: string) => {
    if (tweets.length > 1) {
      setTweets(tweets.filter(t => t.id !== id));
    }
  };

  const updateTweet = (id: string, content: string) => {
    setTweets(tweets.map(t => t.id === id ? { ...t, content } : t));
  };

  const singleInfo = getCharInfo(singleTweet);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Twitter className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Twitter/X Character Counter</h1>
          <p className="text-muted-foreground">
            Count characters and plan your tweets
          </p>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex items-center gap-3">
        <Switch
          id="thread-mode"
          checked={threadMode}
          onCheckedChange={setThreadMode}
        />
        <Label htmlFor="thread-mode">Thread Mode</Label>
      </div>

      {!threadMode ? (
        /* Single Tweet Mode */
        <Card className="glass border-border/50 max-w-2xl">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Compose Tweet</span>
              <Badge variant={singleInfo.remaining < 0 ? 'destructive' : singleInfo.remaining < 20 ? 'secondary' : 'default'}>
                {singleInfo.remaining} remaining
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="What's happening?"
              value={singleTweet}
              onChange={(e) => setSingleTweet(e.target.value)}
              className="min-h-[150px] resize-none text-base"
            />
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className={cn(
                  singleInfo.remaining < 0 ? "text-destructive" :
                  singleInfo.remaining < 20 ? "text-yellow-500" : "text-muted-foreground"
                )}>
                  {singleInfo.count} / {MAX_CHARS}
                </span>
                <span className="text-muted-foreground">
                  {singleInfo.percentage.toFixed(0)}%
                </span>
              </div>
              <Progress 
                value={Math.min(singleInfo.percentage, 100)} 
                className={cn(
                  "h-2",
                  singleInfo.remaining < 0 && "[&>div]:bg-destructive",
                  singleInfo.remaining >= 0 && singleInfo.remaining < 20 && "[&>div]:bg-yellow-500"
                )}
              />
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Thread Mode */
        <div className="space-y-4 max-w-2xl">
          {tweets.map((tweet, index) => {
            const info = getCharInfo(tweet.content);
            return (
              <Card key={tweet.id} className="glass border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span className="flex items-center gap-2">
                      <Badge variant="outline">{index + 1}</Badge>
                      Tweet {index + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant={info.remaining < 0 ? 'destructive' : info.remaining < 20 ? 'secondary' : 'default'}>
                        {info.remaining}
                      </Badge>
                      {tweets.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => removeTweet(tweet.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder={index === 0 ? "Start your thread..." : "Continue your thread..."}
                    value={tweet.content}
                    onChange={(e) => updateTweet(tweet.id, e.target.value)}
                    className="min-h-[100px] resize-none"
                  />
                  <Progress 
                    value={Math.min(info.percentage, 100)} 
                    className={cn(
                      "h-1.5",
                      info.remaining < 0 && "[&>div]:bg-destructive",
                      info.remaining >= 0 && info.remaining < 20 && "[&>div]:bg-yellow-500"
                    )}
                  />
                </CardContent>
              </Card>
            );
          })}
          
          <Button variant="outline" onClick={addTweet} className="w-full gap-2">
            <Plus className="h-4 w-4" />
            Add Tweet to Thread
          </Button>

          {/* Thread Summary */}
          <Card className="glass border-border/50">
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-primary">{tweets.length}</p>
                  <p className="text-xs text-muted-foreground">Tweets</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-primary">
                    {tweets.reduce((sum, t) => sum + t.content.length, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Chars</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-primary">
                    {tweets.filter(t => t.content.length > MAX_CHARS).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Over Limit</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TwitterCounter;